import { getBranches } from "@aitisal/sdk/restaurant";
import { Button, Loader, Menu, Text } from "@mantine/core";
import {
  IconAlertCircle,
  IconChevronDown,
  IconMapPin,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { getTitle } from "../../helper/helper";
import { useRestaurant } from "../../stores/stores";

interface BranchSelectorProps {
  isMobile?: boolean;
}

export function BranchSelector({ isMobile = false }: BranchSelectorProps) {
  const {
    restaurant,
    restaurantId,
    brandId,
    switchBranch,
    userRole,
    inrolledRestaurants,
  } = useRestaurant();
  const [selectedBranch, setSelectedBranch] = useState<any>(null);
  const [userBranches, setUserBranches] = useState<any[]>([]);

  // Only show branch selector for Restaurant Owner role
  if (!userRole?.isOwner) {
    return null;
  }

  // Fetch branches using the getBranches SDK method
  const {
    data: branchesData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["branches", brandId],
    queryFn: async () => {
      if (!brandId) return null;
      const response = await getBranches(brandId);
      return response?.data;
    },
    enabled: !!brandId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Set the current restaurant as selected branch when data loads
  useEffect(() => {
    if (branchesData && userRole.isOwner) {
      setUserBranches(branchesData.items);
    } else if (branchesData && !userRole.isOwner) {
      setUserBranches(
        branchesData.items.filter((branch: any) =>
          inrolledRestaurants?.includes(branch.restaurantId),
        ),
      );
    }

    if (branchesData?.items && restaurantId && !selectedBranch) {
      const currentBranch = branchesData.items.find(
        (branch: any) => branch.restaurantId === restaurantId,
      );
      if (currentBranch) {
        setSelectedBranch(currentBranch);
      }
    }
  }, [branchesData, restaurantId, selectedBranch]);

  // Handle branch selection
  const handleBranchSelect = (branch: any) => {
    setSelectedBranch(branch);
    switchBranch(branch);

    // // Reload the page to ensure all components use the new restaurant context
    window.location.reload();
  };

  // Don't render if no brandId or if there's only one branch
  if (!brandId || userBranches?.length === 1) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-white">
        <Loader size="xs" color="white" />
        {!isMobile && <Text size="sm">Loading branches...</Text>}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center gap-2 text-red-300">
        <IconAlertCircle size={16} />
        {!isMobile && <Text size="sm">Error loading branches</Text>}
      </div>
    );
  }

  if (!branchesData?.items || branchesData.items.length === 0) {
    return null;
  }

  const currentBranchName = selectedBranch
    ? getTitle(selectedBranch.name)
    : restaurant
      ? getTitle(restaurant.name)
      : "Select Branch";

  return (
    <Menu shadow="md" width={280} position="bottom-end" zIndex={99999}>
      <Menu.Target>
        <Button
          variant="subtle"
          color="white"
          size={isMobile ? "xs" : "sm"}
          leftSection={<IconMapPin size={16} />}
          rightSection={<IconChevronDown size={14} />}
          className="text-white hover:bg-gray-700 transition-colors"
          styles={{
            root: {
              border: "1px solid #404040",
              backgroundColor: "transparent",
            },
            label: {
              fontSize: isMobile ? "12px" : "13px",
              fontWeight: 500,
            },
          }}
        >
          {currentBranchName}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>Select Branch</Menu.Label>
        {userBranches?.map((branch: any) => {
          const isSelected =
            selectedBranch?.restaurantId === branch.restaurantId;

          return (
            <Menu.Item
              key={branch.restaurantId}
              onClick={() => handleBranchSelect(branch)}
              leftSection={<IconMapPin size={16} />}
              className={isSelected ? "bg-blue-50" : ""}
              styles={{
                item: {
                  backgroundColor: isSelected ? "#eff6ff" : undefined,
                  fontWeight: isSelected ? 600 : 400,
                },
              }}
            >
              <div>
                <Text size="sm" fw={isSelected ? 600 : 400}>
                  {getTitle(branch.name)}
                </Text>
                {branch.location && (
                  <Text size="xs" c="dimmed">
                    {branch.location.address}, {branch.location.city}
                  </Text>
                )}
              </div>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
}

export default BranchSelector;
